import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { ArticleData } from '@/mocks/articleTypes';
import { categories } from '@/mocks/categories';

interface ArticleTemplateProps {
  article: ArticleData;
}

export default function ArticleTemplate({ article }: ArticleTemplateProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [article.slug]);

  const siteUrl = 'https://vigorbuyspread.fun';
  const articleUrl = `${siteUrl}/${article.slug}`;
  const isoDate = '2026-07-23';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        url: articleUrl,
        datePublished: isoDate,
        dateModified: isoDate,
        author: {
          '@type': 'Organization',
          name: 'VigorBuy Spreadsheet',
          url: siteUrl,
        },
        publisher: {
          '@type': 'Organization',
          name: 'VigorBuy Spreadsheet',
          url: siteUrl,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
      },
      article.faqs.length > 0 ? {
        '@type': 'FAQPage',
        mainEntity: article.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      } : null,
    ].filter(Boolean),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Article Header */}
      <section className="w-full pt-24 md:pt-28 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">Home</Link>
            <i className="ri-arrow-right-s-line text-foreground-400"></i>
            <span className="text-foreground-700 font-medium whitespace-nowrap">{article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}</span>
          </div>
          <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">Guide</span>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            {article.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> Updated July 2026</span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
            <span className="flex items-center gap-1"><i className="ri-book-open-line"></i> Complete Tutorial</span>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="w-full py-10 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {article.sections.map((section, sIdx) => (
            <section key={sIdx} className="mb-10 md:mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">{section.heading}</h2>

              {section.content.map((p, pIdx) => (
                <p key={pIdx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
              ))}

              {section.listItems && !section.isNumbered && (
                <ul className="space-y-2 mb-4">
                  {section.listItems.map((item, iIdx) => (
                    <li key={iIdx} className="flex gap-2 text-sm md:text-base text-foreground-700 leading-relaxed">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {section.listItems && section.isNumbered && (
                <ol className="space-y-3 list-decimal list-inside text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {section.listItems.map((item, iIdx) => (
                    <li key={iIdx}><strong className="text-foreground-900">{item.split(' —')[0]}</strong>{item.includes(' —') ? ' —' + item.split(' —')[1] : ''}</li>
                  ))}
                </ol>
              )}

              {section.tableData && section.tableHeaders && (
                <div className="overflow-x-auto rounded-xl border border-background-200 bg-background-50 mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200 bg-background-100">
                        {section.tableHeaders.map((h, hIdx) => (
                          <th key={hIdx} className="text-left px-4 py-3 font-heading font-semibold text-foreground-800">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.tableData.map((row, rIdx) => (
                        <tr key={rIdx} className={`border-b border-background-100 ${rIdx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/50'}`}>
                          <td className="px-4 py-3 font-medium text-foreground-800">{row.label}</td>
                          <td className="px-4 py-3 text-foreground-600">{row.col2}</td>
                          {row.col3 && <td className="px-4 py-3 text-foreground-600">{row.col3}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* FAQ Section */}
          {article.faqs.length > 0 && (
            <section className="mb-10 md:mb-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">Frequently Asked Questions</h2>
              <div className="space-y-3">
                {article.faqs.map((faq, fIdx) => (
                  <div key={fIdx} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1">{faq.question}</h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA Section */}
          <section className="p-6 md:p-8 rounded-xl bg-primary-500 text-center mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2">
              Ready to Get Started with VigorBuy Spreadsheet?
            </h2>
            <p className="text-sm text-primary-100 mb-6 leading-relaxed max-w-lg mx-auto">
              Visit the official VigorBuy store to explore available spreadsheet templates and start organizing your product data today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto px-6 py-3 text-sm font-semibold text-primary-600 bg-background-50 hover:bg-background-100 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Visit Official Store
              </a>
              <Link
                to="/"
                className="w-full sm:w-auto px-6 py-3 text-sm font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Back to Homepage
              </Link>
            </div>
          </section>

          {/* Internal Links */}
          <nav className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-foreground-500">Related:</span>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">Homepage</Link>
            {categories.slice(0, 4).map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="text-foreground-500 hover:text-primary-600 whitespace-nowrap cursor-pointer"
              >
                {cat.name}
              </a>
            ))}
          </nav>
        </div>
      </article>
    </div>
  );
}